import { useState } from 'react';
import { Link } from 'react-router-dom';
import { LogOut, Receipt } from 'lucide-react';
import type { FulfillmentType, OrderStatus } from '@pizza-roma/shared';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { rememberedOrders } from '../../lib/cart';
import { useAsync } from '../../lib/hooks';
import { dateTime, money } from '../../lib/format';
import { Empty, StatusBadge, TypeBadge } from '../../components/ui';
import { InstallButton } from '../../components/InstallButton';
import { useI18n } from '../../i18n';
import { usePublic } from './context';

interface MyOrder {
  id: string;
  number: number;
  status: OrderStatus;
  fulfillmentType: FulfillmentType;
  totalCents: number;
  itemCount: number;
  createdAt: string;
  publicToken: string;
  restaurant: { slug: string; name: string };
}

export function AccountPage() {
  const { restaurant: r } = usePublic();
  const { t } = useI18n();
  const { me, logout, setMe } = useAuth();
  const [tab, setTab] = useState<'login' | 'register'>('login');
  const [form, setForm] = useState({ email: '', password: '', displayName: '' });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const orders = useAsync(() => (me?.user ? api.get<MyOrder[]>('/api/me/orders') : Promise.resolve([] as MyOrder[])), [me?.user?.id]);
  const local = rememberedOrders(r.slug);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const m =
        tab === 'login'
          ? await api.post<typeof me>('/api/auth/login', { email: form.email, password: form.password })
          : await api.post<typeof me>('/api/auth/register', { ...form, restaurantSlug: r.slug });
      if (m) setMe(m);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t.common.error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="pub-main" style={{ maxWidth: 860 }}>
      <div className="stack-lg">
        {me?.user ? (
          <div className="card card-pad row-between wrap">
            <div>
              <h1 className="section-title">{t.account.hello(me.user.displayName.split(' ')[0])}</h1>
              <p className="muted">{me.user.email}</p>
            </div>
            <div className="row wrap">
              {(me.roles?.length ?? 0) > 0 && (
                <Link className="btn" to="/login">
                  {t.account.staffArea}
                </Link>
              )}
              <button className="btn" onClick={() => void logout()}>
                <LogOut size={16} /> {t.common.signOut}
              </button>
            </div>
          </div>
        ) : (
          <div className="card card-pad stack">
            <div className="tabs">
              <button className={tab === 'login' ? 'active' : ''} onClick={() => setTab('login')}>
                {t.account.signIn}
              </button>
              <button className={tab === 'register' ? 'active' : ''} onClick={() => setTab('register')}>
                {t.account.createAccount}
              </button>
            </div>
            <form className="form-grid" onSubmit={submit}>
              {tab === 'register' && (
                <label className="field">
                  <span>{t.common.name}</span>
                  <input className="input" required value={form.displayName} onChange={(e) => setForm({ ...form, displayName: e.target.value })} />
                </label>
              )}
              <label className="field">
                <span>{t.common.email}</span>
                <input className="input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} autoComplete="email" />
              </label>
              <label className="field">
                <span>
                  {t.common.password} {tab === 'register' && t.account.passwordMin}
                </span>
                <input className="input" type="password" required minLength={tab === 'register' ? 8 : 1} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} autoComplete={tab === 'login' ? 'current-password' : 'new-password'} />
              </label>
              {error && <div className="alert alert-error">{error}</div>}
              <button className="btn btn-primary btn-lg" disabled={busy}>
                {tab === 'login' ? t.account.signInButton : t.account.createButton}
              </button>
              <p className="small muted">{t.account.demoAccount}</p>
            </form>
          </div>
        )}

        <section className="card card-pad stack" id="commandes">
          <h2 className="panel-title">
            <span>
              <Receipt size={18} style={{ verticalAlign: '-3px' }} /> {t.account.myOrders}
            </span>
          </h2>
          {me?.user && (orders.data?.length ?? 0) > 0 ? (
            <div className="table-wrap">
              <table className="table">
                <tbody>
                  {orders.data!.map((o) => (
                    <tr key={o.id}>
                      <td>
                        <b>#{o.number}</b>
                        <div className="muted small">{dateTime(o.createdAt)}</div>
                      </td>
                      <td>
                        <TypeBadge type={o.fulfillmentType} />
                      </td>
                      <td>
                        <StatusBadge status={o.status} />
                      </td>
                      <td className="money">{money(o.totalCents)}</td>
                      <td>
                        <Link className="btn btn-sm" to={`/r/${o.restaurant.slug}/order/${o.publicToken}`}>
                          {t.account.track}
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : local.length > 0 ? (
            <div className="stack">
              <p className="muted small">{t.account.deviceOrders}</p>
              {local.map((o) => (
                <Link key={o.publicToken} className="btn" to={`/r/${r.slug}/order/${o.publicToken}`}>
                  {t.account.orderFrom(dateTime(o.createdAt))}
                </Link>
              ))}
            </div>
          ) : (
            <Empty>{t.account.noOrders}</Empty>
          )}
        </section>

        <section className="card card-pad row-between wrap">
          <div>
            <h3>{t.account.appTitle(r.name)}</h3>
            <p className="muted small">{t.account.appPitch}</p>
          </div>
          <InstallButton className="btn btn-primary" appName={r.name} />
        </section>
      </div>
    </main>
  );
}
